import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, MapPin, Clock, Heart, User, Plus, Check, X, Mail, Phone, Search, Upload, AlertCircle, CheckCircle2, HelpCircle, XCircle, Pencil, Trash2, Eye, ArrowUpDown, FileText, Printer, ChevronLeft, ChevronRight, Download, UserX, UserCheck, Clock as ClockIcon, Ban, Star } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewClientModal from '../components/NewClientModal';
import ViewMatchesModal from '../components/ViewMatchesModal';
import ImportContactsModal from '../components/ImportContactsModal';
import RangeSlider from '../components/RangeSlider';
import { contactApi } from '../lib/api';
import type { Tables } from '../lib/types';
import type { Gender } from '../types/auth';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const genderLabels: Record<string, string> = {
  uomo_etero: 'Uomo Etero',
  donna_etero: 'Donna Etero',
  uomo_gay: 'Uomo Gay',
  donna_gay: 'Donna Gay',
  non_dichiarato: 'Non dichiarato',
};

const genderShortLabels: Record<string, string> = {
  uomo_etero: 'UE',
  donna_etero: 'DE',
  uomo_gay: 'UG',
  donna_gay: 'DG',
  non_dichiarato: 'N/D',
};

interface ClientsTabProps {
  clients: Tables<'contacts'>[];
  onViewMatches: (client: Tables<'contacts'>) => void;
  onEdit: (client: Tables<'contacts'>) => void;
  onDelete: (clientId: string) => void;
  onViewDetails: (client: Tables<'contacts'>) => void;
  onToggleContacted: (clientId: string, contacted: boolean) => void;
  onToggleAllContacted: (contacted: boolean) => void;
  onUpdateConfirmation: (clientId: string, status: 'confirmed' | 'pending' | 'declined') => void;
  onUpdateAllConfirmations: (status: 'confirmed' | 'pending' | 'declined') => void;
  onUpdateNotes: (clientId: string, notes: string) => void;
  sortBy: string;
  onSort: (value: string) => void;
  activeGender: 'all' | Gender;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  totalPages: number;
  onOpenNotesForStatusChange?: (client: Tables<'contacts'>) => void;
}

const ClientsTab: React.FC<ClientsTabProps> = ({
  clients,
  onViewMatches,
  onEdit,
  onDelete,
  onViewDetails,
  onToggleContacted,
  onToggleAllContacted,
  onUpdateConfirmation,
  onUpdateAllConfirmations,
  onUpdateNotes,
  sortBy,
  onSort,
  activeGender,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  totalPages,
  onOpenNotesForStatusChange
}) => {
  const { user } = useAuth();
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [tempNotes, setTempNotes] = useState('');
  const [selectedClientNotes, setSelectedClientNotes] = useState<{
    id: string;
    name: string;
    notes: string | null;
  } | null>(null);
  
  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const allContacted = clients.length > 0 && clients.every(p => p.contacted);

  const handleNotesEdit = (clientId: string, currentNotes: string | null) => {
    setEditingNotes(clientId);
    setTempNotes(currentNotes || '');
  };

  const handleNotesSave = (clientId: string) => {
    onUpdateNotes(clientId, tempNotes);
    setEditingNotes(null);
    setSelectedClientNotes(null);
  };

  const handleNotesCancel = () => {
    setEditingNotes(null);
    setTempNotes('');
  };

  const handleViewNotes = (client: Tables<'contacts'>) => {
    setSelectedClientNotes({
      id: client.id,
      name: client.name,
      notes: client.notes
    });
    setEditingNotes(null);
  };

  return (
    <>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="hidden md:table-cell text-center py-3 px-2 text-sm font-medium text-gray-600 w-12">Stato</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nome</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Età</th>
            <th className="hidden md:table-cell text-left py-3 px-4 text-sm font-medium text-gray-600">Città</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Telefono</th>
            <th className="hidden md:table-cell text-center py-3 px-4 text-sm font-medium text-gray-600">
              <div className="flex flex-col items-center gap-2">
                <span>Contattato</span>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={allContacted}
                    onChange={(e) => onToggleAllContacted(e.target.checked)}
                    className="rounded border-gray-300 text-rose-600 shadow-sm focus:border-rose-300 focus:ring focus:ring-rose-200 focus:ring-opacity-50"
                  />
                </label>
              </div>
            </th>
            <th className="hidden md:table-cell text-center py-3 px-4 text-sm font-medium text-gray-600">
              <div className="flex flex-col items-center gap-2">
                <span>Confermato</span>
                <button
                  onClick={() => onUpdateAllConfirmations('pending')}
                  className="p-1 rounded-full transition-colors hover:bg-yellow-50"
                  title="Imposta tutti in dubbio"
                >
                  <HelpCircle className="h-5 w-5 text-yellow-500" />
                </button>
              </div>
            </th>
            <th className="hidden md:table-cell w-[12%] text-center py-3 px-4 text-sm font-medium text-gray-600">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {clients.length > 0 ? (
            clients.map((client) => (
              <tr key={client.id} className="border-b border-gray-100">
                <td className="hidden md:table-cell py-3 px-2 text-center">
                  <div className="flex justify-center">
                    {(!client.contact_status || client.contact_status === 'attivo') && (
                      <div
                        className="w-3 h-3 rounded-full bg-green-500"
                        title="Attivo"
                      />
                    )}
                    {client.contact_status === 'non_interessato' && (
                      <div
                        className="w-3 h-3 rounded-full bg-red-500"
                        title="Non interessato"
                      />
                    )}
                    {client.contact_status === 'da_ricontattare' && (
                      <div
                        className="w-3 h-3 rounded-full bg-yellow-500"
                        title="Da controllare"
                      />
                    )}
                    {client.contact_status === 'in_una_relazione' && (
                      <div
                        className="w-3 h-3 rounded-full bg-blue-500"
                        title="In una relazione"
                      />
                    )}
                    {client.contact_status === 'blacklist' && (
                      <div
                        className="w-3 h-3 rounded-full bg-gray-800"
                        title="Blacklist"
                      />
                    )}
                    {client.contact_status === 'vip' && (
                      <Star
                        className="h-3.5 w-3.5 text-green-500 fill-green-500"
                        title="VIP"
                      />
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => onViewDetails(client)}
                    className="font-medium hover:text-rose-600 transition-colors text-left"
                  >
                    {client.name}
                  </button>
                </td>
                <td className="py-3 px-4">{calculateAge(client.birthdate)}</td>
                <td className="hidden md:table-cell py-3 px-4">{client.city}</td>
                <td className="py-3 px-4">
                  <span className="hidden md:inline">{client.phone}</span>
                  <span className="md:hidden">
                    {client.phone && client.phone.length > 10
                      ? `${client.phone.substring(0, 6)}...`
                      : client.phone}
                  </span>
                </td>
                <td className="hidden md:table-cell py-3 px-4 text-center">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={client.contacted}
                      onChange={(e) => onToggleContacted(client.id, e.target.checked)}
                      className="rounded border-gray-300 text-rose-600 shadow-sm focus:border-rose-300 focus:ring focus:ring-rose-200 focus:ring-opacity-50"
                    />
                  </label>
                </td>
                <td className="hidden md:table-cell py-3 px-4">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => onUpdateConfirmation(client.id, 'confirmed')}
                      className={`p-1 rounded-full transition-colors ${
                        client.confirmation_status === 'confirmed' ? 'bg-green-100' : 'hover:bg-green-50'
                      }`}
                      title="Confermato"
                    >
                      <CheckCircle2 className={`h-5 w-5 ${
                        client.confirmation_status === 'confirmed' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                    </button>
                    <button
                      onClick={() => onUpdateConfirmation(client.id, 'pending')}
                      className={`p-1 rounded-full transition-colors ${
                        client.confirmation_status === 'pending' ? 'bg-yellow-100' : 'hover:bg-yellow-50'
                      }`}
                      title="In dubbio"
                    >
                      <HelpCircle className={`h-5 w-5 ${
                        client.confirmation_status === 'pending' ? 'text-yellow-500' : 'text-gray-400'
                      }`} />
                    </button>
                    <button
                      onClick={() => onUpdateConfirmation(client.id, 'declined')}
                      className={`p-1 rounded-full transition-colors ${
                        client.confirmation_status === 'declined' ? 'bg-red-100' : 'hover:bg-red-50'
                      }`}
                      title="Non confermato"
                    >
                      <XCircle className={`h-5 w-5 ${
                        client.confirmation_status === 'declined' ? 'text-red-500' : 'text-gray-400'
                      }`} />
                    </button>
                  </div>
                </td>
                <td className="hidden md:table-cell w-[12%] py-3 px-4">
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => handleViewNotes(client)}
                      className={`p-1 rounded ${
                        client.notes && client.notes.trim() !== ''
                          ? (!client.contact_status || client.contact_status === 'attivo')
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : client.contact_status === 'da_ricontattare'
                            ? 'bg-yellow-400 text-white hover:bg-yellow-500'
                            : client.contact_status === 'in_una_relazione'
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : client.contact_status === 'non_interessato'
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : client.contact_status === 'blacklist'
                            ? 'bg-gray-900 text-white hover:bg-black'
                            : client.contact_status === 'vip'
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-yellow-400 text-white hover:bg-yellow-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      title="Note"
                    >
                      <FileText className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onViewMatches(client)}
                      className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                      title="Visualizza Match"
                    >
                      <Heart className="h-5 w-5" />
                    </button>
                    {user?.role === 'admin' && (
                      <>
                        <button
                          onClick={() => onEdit(client)}
                          className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                          title="Modifica"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => onDelete(client.id)}
                          className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                          title="Elimina"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="md:hidden py-8 text-center text-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Nessun cliente</p>
              </td>
              <td colSpan={8} className="hidden md:table-cell py-8 text-center text-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Nessun cliente</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination controls */}
      <div className="mt-6">
        {/* Mobile: Layout compatto */}
        <div className="md:hidden flex items-center justify-between gap-4">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-sm text-gray-600 whitespace-nowrap">
            {currentPage} / {totalPages}
          </div>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop: Layout completo con tutti i numeri */}
        <div className="hidden md:flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === page
                    ? 'bg-rose-600 text-white'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Notes Modal */}
      {selectedClientNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Note di {selectedClientNotes.name}</h2>
              <button
                onClick={() => setSelectedClientNotes(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {editingNotes === selectedClientNotes.id ? (
              <div className="space-y-4">
                <textarea
                  value={tempNotes}
                  onChange={(e) => setTempNotes(e.target.value)}
                  className="w-full h-40 rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  placeholder="Inserisci le note..."
                />
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleNotesCancel}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => handleNotesSave(selectedClientNotes.id)}
                    className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
                  >
                    Salva
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg min-h-[10rem] whitespace-pre-wrap">
                  {selectedClientNotes.notes || 'Nessuna nota'}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => handleNotesEdit(selectedClientNotes.id, selectedClientNotes.notes)}
                    className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
                  >
                    Modifica
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<Tables<'contacts'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Tables<'contacts'> | null>(null);
  const [editingClient, setEditingClient] = useState<Tables<'contacts'> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [creatorInfo, setCreatorInfo] = useState<any>(null);
  const [updaterInfo, setUpdaterInfo] = useState<any>(null);
  const [loadingCreator, setLoadingCreator] = useState(false);
  const [loadingUpdater, setLoadingUpdater] = useState(false);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [activeGender, setActiveGender] = useState<'all' | Gender>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [ageRange, setAgeRange] = useState<[number, number]>([18, 120]);
  const [totalClients, setTotalClients] = useState(0);
  const [activeStatusFilter, setActiveStatusFilter] = useState<'all' | 'attivo' | 'non_interessato' | 'da_ricontattare' | 'in_una_relazione' | 'blacklist' | 'vip'>('all');
  const [editingNotesForStatusChange, setEditingNotesForStatusChange] = useState<Tables<'contacts'> | null>(null);
  const [tempStatusNotes, setTempStatusNotes] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState<'non_interessato' | 'da_ricontattare' | 'in_una_relazione' | 'blacklist' | 'vip' | null>(null);
  const isBatchUpdatingRef = useRef(false);
  const [isSavingStatusChange, setIsSavingStatusChange] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  // Update total count when clients list changes
  useEffect(() => {
    setTotalClients(clients.length);
  }, [clients]);


  useEffect(() => {
    if (selectedClient?.created_by) {
      loadCreatorInfo(selectedClient.created_by);
    } else {
      setCreatorInfo(null);
    }

    if (selectedClient?.updated_by) {
      loadUpdaterInfo(selectedClient.updated_by);
    } else {
      setUpdaterInfo(null);
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const clientsData = await contactApi.getAll();
      clientsData.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setClients(clientsData);
      setTotalClients(clientsData.length);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      setError(error.message || 'Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const loadCreatorInfo = async (creatorId: string) => {
    try {
      setLoadingCreator(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('name, role')
        .eq('id', creatorId)
        .single();

      if (error) throw error;
      setCreatorInfo(profile);
    } catch (error) {
      console.error('Error loading creator info:', error);
      setCreatorInfo(null);
    } finally {
      setLoadingCreator(false);
    }
  };

  const loadUpdaterInfo = async (updaterId: string) => {
    try {
      setLoadingUpdater(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('name, role')
        .eq('id', updaterId)
        .single();

      if (error) throw error;
      setUpdaterInfo(profile);
    } catch (error) {
      console.error('Error loading updater info:', error);
      setUpdaterInfo(null);
    } finally {
      setLoadingUpdater(false);
    }
  };

  const handleSort = (value: string) => {
    setSortBy(value);
    const sortedClients = [...clients];

    switch (value) {
      case 'name':
        sortedClients.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'age':
        sortedClients.sort((a, b) => {
          const ageA = calculateAge(a.birthdate);
          const ageB = calculateAge(b.birthdate);
          return ageA - ageB;
        });
        break;
      case 'updated_at':
        sortedClients.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });
        break;
      case 'created_at':
        sortedClients.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
      case 'confirmation':
        sortedClients.sort((a, b) => {
          const statusOrder = { confirmed: 0, pending: 1, declined: 2 };
          const statusA = statusOrder[a.confirmation_status as keyof typeof statusOrder] ?? 3;
          const statusB = statusOrder[b.confirmation_status as keyof typeof statusOrder] ?? 3;
          return statusA - statusB;
        });
        break;
    }

    setClients(sortedClients);
  };

  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleNewClient = async (clientData: any) => {
    try {
      setError(null);

      const updateData: any = {
        name: clientData.name.toUpperCase(),
        email: clientData.email,
        phone: clientData.phone,
        gender: clientData.gender,
        birthdate: clientData.birthdate,
        city: clientData.city,
        contact_status: clientData.contact_status || 'attivo',
        status_notes: clientData.status_notes || ''
      };

      if (clientData.contact_status === 'non_interessato' || clientData.contact_status === 'blacklist') {
        updateData.opt_out_date = new Date().toISOString();
      }

      if (editingClient) {
        await contactApi.update(editingClient.id, updateData);
      } else {
        const newClient = await contactApi.create({
          ...updateData,
          contacted: false,
          confirmation_status: 'pending',
          notes: null
        });

        if (newClient?.id) {
          setClients(prev => {
            const exists = prev.some(p => p.id === newClient.id);
            if (exists) return prev;
            return [newClient, ...prev];
          });
        } else {
          await loadClients();
        }
      }

    } catch (error: any) {
      console.error('Error saving client:', error);
      throw error;
    }
  };

  const handleEdit = (client: Tables<'contacts'>) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const handleDelete = async (clientId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo cliente?')) {
      try {
        // Remove from local state immediately for instant feedback
        setClients(prev => prev.filter(p => p.id !== clientId));

        // Delete from database
        await contactApi.delete(clientId);
      } catch (error: any) {
        console.error('Error deleting client:', error);
        setError(error.message || 'Errore nell\'eliminazione del cliente');

        // Reload on error to restore correct state
        await loadClients();
      }
    }
  };

  const handleToggleContacted = async (clientId: string, contacted: boolean) => {
    setClients(prevClients =>
      prevClients.map(p =>
        p.id === clientId ? { ...p, contacted } : p
      )
    );

    try {
      await contactApi.update(clientId, { contacted });
    } catch (error: any) {
      console.error('Error updating contacted status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato contattato');
      setClients(prevClients =>
        prevClients.map(p =>
          p.id === clientId ? { ...p, contacted: !contacted } : p
        )
      );
    }
  };

  const handleToggleAllContacted = async (contacted: boolean) => {
    const clientIds = filteredClients.map(p => p.id);

    setClients(prev =>
      prev.map(p => clientIds.includes(p.id) ? { ...p, contacted } : p)
    );
    isBatchUpdatingRef.current = true;

    try {
      await contactApi.updateBatch(clientIds, { contacted });
      const freshData = await contactApi.getAll();
      freshData.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setClients(freshData);
    } catch (error: any) {
      console.error('Error updating all contacted status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato contattato');
      const freshData = await contactApi.getAll().catch(() => null);
      if (freshData) {
        freshData.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setClients(freshData);
      }
    } finally {
      setTimeout(() => { isBatchUpdatingRef.current = false; }, 3000);
    }
  };

  const handleUpdateConfirmation = async (clientId: string, status: 'confirmed' | 'pending' | 'declined') => {
    const previousStatus = clients.find(p => p.id === clientId)?.confirmation_status;

    setClients(prevClients =>
      prevClients.map(p =>
        p.id === clientId ? { ...p, confirmation_status: status } : p
      )
    );

    try {
      await contactApi.update(clientId, { confirmation_status: status });
    } catch (error: any) {
      console.error('Error updating confirmation status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato di conferma');
      if (previousStatus !== undefined) {
        setClients(prevClients =>
          prevClients.map(p =>
            p.id === clientId ? { ...p, confirmation_status: previousStatus } : p
          )
        );
      }
    }
  };

  const handleUpdateAllConfirmations = async (status: 'confirmed' | 'pending' | 'declined') => {
    const clientIds = filteredClients.map(p => p.id);

    setClients(prev =>
      prev.map(p => clientIds.includes(p.id) ? { ...p, confirmation_status: status } : p)
    );
    isBatchUpdatingRef.current = true;

    try {
      await contactApi.updateBatch(clientIds, { confirmation_status: status });
      const freshData = await contactApi.getAll();
      freshData.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setClients(freshData);
    } catch (error: any) {
      console.error('Error updating all confirmation status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato di conferma');
      const freshData = await contactApi.getAll().catch(() => null);
      if (freshData) {
        freshData.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setClients(freshData);
      }
    } finally {
      setTimeout(() => { isBatchUpdatingRef.current = false; }, 3000);
    }
  };

  const handleUpdateNotes = async (clientId: string, notes: string) => {
    try {
      await contactApi.update(clientId, { notes });

      setClients(prevClients =>
        prevClients.map(p =>
          p.id === clientId ? { ...p, notes } : p
        )
      );
    } catch (error: any) {
      console.error('Error updating notes:', error);
      setError(error.message || 'Errore nell\'aggiornamento delle note');
    }
  };

  const handleUpdateContactStatus = async (
    clientId: string,
    status: 'attivo' | 'non_interessato' | 'da_ricontattare' | 'in_una_relazione' | 'blacklist' | 'vip',
    statusNotes?: string
  ) => {
    try {
      const updateData: any = {
        contact_status: status,
        status_notes: statusNotes || ''
      };

      if (status === 'non_interessato' || status === 'blacklist') {
        updateData.opt_out_date = new Date().toISOString();
      } else {
        updateData.opt_out_date = null;
      }

      await contactApi.update(clientId, updateData);

      setClients(prevClients =>
        prevClients.map(p =>
          p.id === clientId ? { ...p, ...updateData } : p
        )
      );

      if (selectedClient?.id === clientId) {
        setSelectedClient(prev => prev ? { ...prev, ...updateData } : null);
      }
    } catch (error: any) {
      console.error('Error updating contact status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato contatto');
    }
  };

  const handleOpenNotesForStatusChange = (client: Tables<'contacts'>, newStatus: 'non_interessato' | 'da_ricontattare' | 'in_una_relazione' | 'blacklist') => {
    setEditingNotesForStatusChange(client);
    setTempStatusNotes(client.notes || '');
    setPendingStatusChange(newStatus);
  };

  const handleSaveNotesForStatusChange = async () => {
    if (!editingNotesForStatusChange || !pendingStatusChange || isSavingStatusChange) return;

    setIsSavingStatusChange(true);
    setError(null);

    try {
      const updateData: any = {
        contact_status: pendingStatusChange,
        notes: tempStatusNotes
      };

      if (pendingStatusChange === 'non_interessato' || pendingStatusChange === 'blacklist') {
        updateData.opt_out_date = new Date().toISOString();
      } else {
        updateData.opt_out_date = null;
      }

      console.log('Updating contact with data:', updateData);

      await contactApi.update(editingNotesForStatusChange.id, updateData);

      console.log('Contact updated successfully');

      setClients(prevClients =>
        prevClients.map(p =>
          p.id === editingNotesForStatusChange.id ? { ...p, ...updateData } : p
        )
      );

      if (selectedClient?.id === editingNotesForStatusChange.id) {
        setSelectedClient(prev => prev ? { ...prev, ...updateData } : null);
      }

      setEditingNotesForStatusChange(null);
      setTempStatusNotes('');
      setPendingStatusChange(null);
    } catch (error: any) {
      console.error('Error updating contact status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato contatto');
    } finally {
      setIsSavingStatusChange(false);
    }
  };

  const handleViewMatches = (client: Tables<'contacts'>) => {
    setSelectedClient(client);
    setIsMatchesModalOpen(true);
  };

  const handleViewDetails = (client: Tables<'contacts'>) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add title
    doc.setFontSize(20);
    doc.text('Partecipanti Confermati', pageWidth/2, 20, { align: 'center' });

    // Add date
    doc.setFontSize(12);
    doc.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, 20, 30);

    // Table headers
    const headers = ['Nome', 'Età', 'Sesso', 'Creato da'];
    let y = 40;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    headers.forEach((header, i) => {
      doc.text(header, 20 + (i * 45), y);
    });

    // Table content
    doc.setFont('helvetica', 'normal');
    const confirmedClients = filteredClients.filter(p => p.confirmation_status === 'confirmed');

    // Use for...of instead of forEach to properly handle async/await
    for (const client of confirmedClients) {
      y = y + 10;

      // Add new page if needed
      if (y > 280) {
        doc.addPage();
        y = 20;
      }

      try {
        // Get creator info
        const { data: creator } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', client.created_by)
          .single();

        doc.text(client.name.substring(0, 20), 20, y);
        doc.text(calculateAge(client.birthdate).toString(), 65, y);
        doc.text(genderShortLabels[client.gender] || client.gender, 110, y);
        doc.text(creator?.name || 'N/A', 155, y);
      } catch (error) {
        console.error('Error fetching creator info:', error);
        doc.text(client.name.substring(0, 20), 20, y);
        doc.text(calculateAge(client.birthdate).toString(), 65, y);
        doc.text(genderShortLabels[client.gender] || client.gender, 110, y);
        doc.text('N/A', 155, y);
      }
    }

    // Save the PDF
    doc.save('clienti-confermati.pdf');
  };

  const exportToExcel = () => {
    const dataToExport = filteredClients.map(client => ({
      'Nome': client.name,
      'Email': client.email,
      'Telefono': client.phone,
      'Sesso': genderLabels[client.gender] || client.gender,
      'Età': calculateAge(client.birthdate),
      'Data di Nascita': new Date(client.birthdate).toLocaleDateString('it-IT'),
      'Città': client.city,
      'Contattato': client.contacted ? 'Sì' : 'No',
      'Stato Conferma': client.confirmation_status === 'confirmed' ? 'Confermato' :
                        client.confirmation_status === 'declined' ? 'Non confermato' : 'In dubbio',
      'Note': client.notes || '',
      'Data Creazione': new Date(client.created_at).toLocaleString('it-IT')
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clienti');

    const fileName = `clienti_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const filteredClients = clients.filter(client => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      client.name.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      client.phone.toLowerCase().includes(searchLower) ||
      client.city.toLowerCase().includes(searchLower);

    const age = calculateAge(client.birthdate);
    const matchesAge = age >= ageRange[0] && age <= ageRange[1];

    const matchesStatus = activeStatusFilter === 'all'
      || (client.contact_status || 'attivo') === activeStatusFilter;

    return matchesSearch && (activeGender === 'all' || client.gender === activeGender) && matchesAge && matchesStatus;
  });

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeGender, itemsPerPage, ageRange, activeStatusFilter]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  const confirmedCount = clients.filter(p => p.confirmation_status === 'confirmed').length;
  const pendingCount = clients.filter(p => p.confirmation_status === 'pending').length;
  const contactedCount = clients.filter(p => p.contacted).length;

  const contactedUE = clients.filter(p => p.contacted && p.gender === 'uomo_etero').length;
  const contactedDE = clients.filter(p => p.contacted && p.gender === 'donna_etero').length;
  const contactedUG = clients.filter(p => p.contacted && p.gender === 'uomo_gay').length;
  const contactedDG = clients.filter(p => p.contacted && p.gender === 'donna_gay').length;

  const confirmedUE = clients.filter(p => p.confirmation_status === 'confirmed' && p.gender === 'uomo_etero').length;
  const confirmedDE = clients.filter(p => p.confirmation_status === 'confirmed' && p.gender === 'donna_etero').length;
  const confirmedUG = clients.filter(p => p.confirmation_status === 'confirmed' && p.gender === 'uomo_gay').length;
  const confirmedDG = clients.filter(p => p.confirmation_status === 'confirmed' && p.gender === 'donna_gay').length;

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          {/* Desktop: Layout originale 4 colonne */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Contattati U.E.</p>
                  <p className="text-3xl font-bold text-white mt-1">{contactedUE}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Contattate D.E.</p>
                  <p className="text-3xl font-bold text-white mt-1">{contactedDE}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Contattati U.G.</p>
                  <p className="text-3xl font-bold text-white mt-1">{contactedUG}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Contattate D.G.</p>
                  <p className="text-3xl font-bold text-white mt-1">{contactedDG}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Confermati U.E.</p>
                  <p className="text-3xl font-bold text-white mt-1">{confirmedUE}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Confermate D.E.</p>
                  <p className="text-3xl font-bold text-white mt-1">{confirmedDE}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Confermati U.G.</p>
                  <p className="text-3xl font-bold text-white mt-1">{confirmedUG}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Confermate D.G.</p>
                  <p className="text-3xl font-bold text-white mt-1">{confirmedDG}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Griglia 2x4 senza icone */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {/* Riga 1: Contattati U.E. | Confermati U.E. */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Contattati U.E.</p>
              <p className="text-2xl font-bold text-white mt-1">{contactedUE}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Confermati U.E.</p>
              <p className="text-2xl font-bold text-white mt-1">{confirmedUE}</p>
            </div>

            {/* Riga 2: Contattate D.E. | Confermate D.E. */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Contattate D.E.</p>
              <p className="text-2xl font-bold text-white mt-1">{contactedDE}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Confermate D.E.</p>
              <p className="text-2xl font-bold text-white mt-1">{confirmedDE}</p>
            </div>

            {/* Riga 3: Contattati U.G. | Confermati U.G. */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Contattati U.G.</p>
              <p className="text-2xl font-bold text-white mt-1">{contactedUG}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Confermati U.G.</p>
              <p className="text-2xl font-bold text-white mt-1">{confirmedUG}</p>
            </div>

            {/* Riga 4: Contattate D.G. | Confermate D.G. */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Contattate D.G.</p>
              <p className="text-2xl font-bold text-white mt-1">{contactedDG}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <p className="text-white/80 text-xs font-medium">Confermate D.G.</p>
              <p className="text-2xl font-bold text-white mt-1">{confirmedDG}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Cerca clienti..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
            />
          </div>
        <button
          onClick={generatePDF}
          className="hidden md:flex bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all items-center gap-2 text-sm font-medium shadow-lg"
        >
          <Printer className="h-4 w-4" />
          Stampa Confermati
        </button>
        <button
          onClick={exportToExcel}
          className="hidden md:flex bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all items-center gap-2 text-sm font-medium shadow-lg"
        >
          <Download className="h-4 w-4" />
          Esporta Contatti
        </button>
        {user?.role !== 'pr' && (
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="hidden md:flex bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all items-center gap-2 text-sm font-medium shadow-lg"
          >
            <Upload className="h-4 w-4" />
            Importa Contatti
          </button>
        )}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all flex items-center gap-2 text-sm font-medium shadow-lg whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            Nuovo Cliente
          </button>
        </div>

      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <div className="flex items-center gap-6 px-4 py-2 flex-wrap">
            {/* Desktop: Bottoni */}
            <nav className="hidden md:flex space-x-3" aria-label="Tabs">
              <button
                onClick={() => setActiveGender('all')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'all'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tutti ({totalClients})
              </button>
              <button
                onClick={() => setActiveGender('uomo_etero')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'uomo_etero'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Uomini Etero ({clients.filter(p => p.gender === 'uomo_etero').length})
              </button>
              <button
                onClick={() => setActiveGender('donna_etero')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'donna_etero'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Donne Etero ({clients.filter(p => p.gender === 'donna_etero').length})
              </button>
              <button
                onClick={() => setActiveGender('uomo_gay')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'uomo_gay'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Uomini Gay ({clients.filter(p => p.gender === 'uomo_gay').length})
              </button>
              <button
                onClick={() => setActiveGender('donna_gay')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'donna_gay'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Donne Gay ({clients.filter(p => p.gender === 'donna_gay').length})
              </button>
              <button
                onClick={() => setActiveGender('non_dichiarato')}
                className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeGender === 'non_dichiarato'
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Non Dichiarato ({clients.filter(p => p.gender === 'non_dichiarato').length})
              </button>
            </nav>

            {/* Mobile: Dropdowns */}
            <div className="md:hidden w-full py-2 space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Sesso</label>
                <select
                  value={activeGender}
                  onChange={(e) => setActiveGender(e.target.value as 'all' | Gender)}
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm font-medium"
                >
                  <option value="all">Tutti ({totalClients})</option>
                  <option value="uomo_etero">Uomini Etero ({clients.filter(p => p.gender === 'uomo_etero').length})</option>
                  <option value="donna_etero">Donne Etero ({clients.filter(p => p.gender === 'donna_etero').length})</option>
                  <option value="uomo_gay">Uomini Gay ({clients.filter(p => p.gender === 'uomo_gay').length})</option>
                  <option value="donna_gay">Donne Gay ({clients.filter(p => p.gender === 'donna_gay').length})</option>
                  <option value="non_dichiarato">Non Dichiarato ({clients.filter(p => p.gender === 'non_dichiarato').length})</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Stato</label>
                <select
                  value={activeStatusFilter}
                  onChange={(e) => setActiveStatusFilter(e.target.value as typeof activeStatusFilter)}
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm font-medium"
                >
                  <option value="all">Tutti ({clients.length})</option>
                  <option value="attivo">Attivi ({clients.filter(p => (p.contact_status || 'attivo') === 'attivo').length})</option>
                  <option value="da_ricontattare">Da controllare ({clients.filter(p => p.contact_status === 'da_ricontattare').length})</option>
                  <option value="in_una_relazione">In una relazione ({clients.filter(p => p.contact_status === 'in_una_relazione').length})</option>
                  <option value="non_interessato">Non interessati ({clients.filter(p => p.contact_status === 'non_interessato').length})</option>
                  <option value="blacklist">Blacklist ({clients.filter(p => p.contact_status === 'blacklist').length})</option>
                  <option value="vip">VIP ({clients.filter(p => p.contact_status === 'vip').length})</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 py-2 min-w-[200px] w-full md:w-auto">
              <label className="text-sm text-gray-600 whitespace-nowrap">Età:</label>
              <div className="flex-1 px-2">
                <RangeSlider
                  min={18}
                  max={120}
                  value={ageRange}
                  onChange={setAgeRange}
                />
              </div>
            </div>

            {/* Desktop: Solo Selects con tooltip */}
            <div className="hidden md:flex items-center gap-4 ml-auto">
              <div className="flex items-center gap-2">
                <select
                  value={activeStatusFilter}
                  onChange={(e) => setActiveStatusFilter(e.target.value as typeof activeStatusFilter)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm"
                  title="Filtra per stato"
                >
                  <option value="all">Tutti ({clients.length})</option>
                  <option value="attivo">Attivi ({clients.filter(p => (p.contact_status || 'attivo') === 'attivo').length})</option>
                  <option value="da_ricontattare">Da controllare ({clients.filter(p => p.contact_status === 'da_ricontattare').length})</option>
                  <option value="in_una_relazione">In una relazione ({clients.filter(p => p.contact_status === 'in_una_relazione').length})</option>
                  <option value="non_interessato">Non interessati ({clients.filter(p => p.contact_status === 'non_interessato').length})</option>
                  <option value="blacklist">Blacklist ({clients.filter(p => p.contact_status === 'blacklist').length})</option>
                  <option value="vip">VIP ({clients.filter(p => p.contact_status === 'vip').length})</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => handleSort(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm"
                  title="Ordina per"
                >
                  <option value="created_at">Data Creazione</option>
                  <option value="updated_at">Ultima Modifica</option>
                  <option value="confirmation">Confermati</option>
                  <option value="age">Età</option>
                  <option value="name">Nome</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm"
                  title="Elementi per pagina"
                >
                  <option value={100}>100</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
            </div>

            {/* Mobile: Solo Selects compatti */}
            <div className="md:hidden flex items-center gap-2 w-full">
              <select
                value={sortBy}
                onChange={(e) => handleSort(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm"
              >
                <option value="created_at">Data Creazione</option>
                <option value="updated_at">Ultima Modifica</option>
                <option value="confirmation">Confermati</option>
                <option value="age">Per Età</option>
                <option value="name">Per Nome</option>
              </select>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 text-sm"
              >
                <option value={100}>100/pagina</option>
                <option value={1000}>1000/pagina</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4">
          <ClientsTab
            clients={paginatedClients}
            onViewMatches={handleViewMatches}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
            onToggleContacted={handleToggleContacted}
            onToggleAllContacted={handleToggleAllContacted}
            onUpdateConfirmation={handleUpdateConfirmation}
            onUpdateAllConfirmations={handleUpdateAllConfirmations}
            onUpdateNotes={handleUpdateNotes}
            sortBy={sortBy}
            onSort={handleSort}
            activeGender={activeGender}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            totalPages={totalPages}
          />
        </div>
      </div>

      <NewClientModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingClient(null);
        }}
        onSubmit={handleNewClient}
        initialData={editingClient}
      />

      <ViewMatchesModal
        isOpen={isMatchesModalOpen}
        onClose={() => {
          setIsMatchesModalOpen(false);
          setSelectedClient(null);
        }}
        participant={selectedClient!}
      />

      <ImportContactsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={loadClients}
        city={user?.city || 'ROMA'}
      />

      {/* Details Modal */}
      {isDetailsModalOpen && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Dettagli Cliente</h2>
              <button
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedClient(null);
                  setCreatorInfo(null);
                  setUpdaterInfo(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-50 rounded-full">
                  <User className="h-5 w-5 text-rose-500" />
                </div>
                <div>
                  <p className="font-medium">{selectedClient.name}</p>
                  <p className="text-sm text-gray-500 capitalize">
                    {genderLabels[selectedClient.gender] || selectedClient.gender}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.city}</span>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Data di Nascita</p>
                    <p className="font-medium">{new Date(selectedClient.birthdate).toLocaleDateString('it-IT')}</p>
                    <p className="text-sm text-gray-500">({calculateAge(selectedClient.birthdate)} anni)</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-500 mb-2">Stato Contatto</p>
                <div className="space-y-2">
                  <select
                    value={selectedClient.contact_status || 'attivo'}
                    onChange={(e) => {
                      const newStatus = e.target.value as 'attivo' | 'non_interessato' | 'da_ricontattare' | 'in_una_relazione' | 'blacklist' | 'vip';
                      if (newStatus === 'non_interessato' || newStatus === 'blacklist' || newStatus === 'da_ricontattare' || newStatus === 'in_una_relazione') {
                        handleOpenNotesForStatusChange(selectedClient, newStatus);
                      } else {
                        handleUpdateContactStatus(selectedClient.id, newStatus);
                      }
                    }}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  >
                    <option value="attivo">Attivo</option>
                    <option value="da_ricontattare">Da controllare</option>
                    <option value="in_una_relazione">In una relazione</option>
                    <option value="non_interessato">Non interessato</option>
                    <option value="blacklist">Blacklist</option>
                    <option value="vip">VIP</option>
                  </select>
                  {selectedClient.status_notes && (
                    <div className="p-2 bg-gray-50 rounded text-sm">
                      <p className="font-medium text-gray-700">Note:</p>
                      <p className="text-gray-600">{selectedClient.status_notes}</p>
                    </div>
                  )}
                  {selectedClient.opt_out_date && (
                    <p className="text-xs text-gray-500">
                      Data opt-out: {new Date(selectedClient.opt_out_date).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              </div>

              {selectedClient.created_by && creatorInfo && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-500 mb-2">Scheda creata da</p>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{creatorInfo.name}</span>
                    <span className="text-sm text-gray-500 capitalize">({creatorInfo.role})</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {new Date(selectedClient.created_at).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })} alle {new Date(selectedClient.created_at).toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}

              {selectedClient.updated_by && updaterInfo && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-500 mb-2">Ultima modifica</p>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{updaterInfo.name}</span>
                    <span className="text-sm text-gray-500 capitalize">({updaterInfo.role})</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {new Date(selectedClient.updated_at).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })} alle {new Date(selectedClient.updated_at).toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal for Status Change */}
      {editingNotesForStatusChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Note per {editingNotesForStatusChange.name}</h2>
              <button
                onClick={() => {
                  setEditingNotesForStatusChange(null);
                  setTempStatusNotes('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Scrivi qui il motivo per il cambio di stato contatto:
              </p>
              <textarea
                value={tempStatusNotes}
                onChange={(e) => setTempStatusNotes(e.target.value)}
                className="w-full h-40 rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                placeholder="Inserisci le note..."
                autoFocus
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEditingNotesForStatusChange(null);
                    setTempStatusNotes('');
                    setPendingStatusChange(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveNotesForStatusChange}
                  disabled={isSavingStatusChange}
                  className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingStatusChange ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;